import { useChangeEnrollmentStatus } from '@actions';
import TextArea from '@components/V4/Inputs/Textarea';
import Select from '@components/V4/Select/Select';
import { zodResolver } from '@hookform/resolvers/zod';
import { Enrollment, ENROLLMENT_STATUS_OPTIONS, EnrollmentStatusTypes } from '@shared/common';
import { changeEnrollmentStatusValidation } from '@shared/validations';
import useToast from 'apps/agora/src/hooks/useToast';
import React, { useEffect, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { z } from 'zod';
import SessionModalFormWrapper from '../../DetailsModalFormWrapper';

interface EnrollmentDetailsStatusFormProps {
  defaultData: Enrollment;
  onCancel: () => void;
}

const formDataSchema = changeEnrollmentStatusValidation;

type FormData = z.infer<typeof formDataSchema>;

const EnrollmentDetailsStatusForm = (props: EnrollmentDetailsStatusFormProps) => {
  const { defaultData, onCancel } = props;

  const toast = useToast();

  const [initialReason, setInitialReason] = useState<string>(defaultData?.lastStatus?.reason ?? '');

  const { handleSubmit, trigger, setValue, control, getValues, register, watch } =
    useForm<FormData>({
      resolver: zodResolver(formDataSchema),
      reValidateMode: 'onBlur',
      mode: 'onBlur',
      defaultValues: {
        newStatus: defaultData?.lastStatus?.status,
        reason: defaultData?.lastStatus?.reason ?? '',
      },
    });

  const { field: statusField, fieldState: statusFieldState } = useController({
    name: 'newStatus',
    control,
  });

  const { mutate: changeEnrollmentStatus, isLoading } = useChangeEnrollmentStatus(defaultData._id, {
    onSuccess: async () => {
      toast.success('Successfully changed the enrollment status.');
      onCancel();
    },
    onError: () => {
      toast.error('Enrollment status could not be changed.');
    },
  });

  const onSubmit = async (data: FormData) => {
    const isValid = await trigger();
    if (!isValid) return;

    if (
      defaultData?.lastStatus?.status === data.newStatus &&
      defaultData?.lastStatus?.reason === data.reason
    ) {
      toast.info('Status is not changed');
      return;
    }

    changeEnrollmentStatus({
      newStatus: data.newStatus as EnrollmentStatusTypes,
      ...(data.reason !== initialReason &&
        data.reason && {
          reason: data.reason,
        }),
    });
  };

  const touchAllFields = (fields: Record<keyof FormData, string | EnrollmentStatusTypes>) => {
    Object.keys(fields).forEach((key) => {
      setValue(key as keyof FormData, getValues()[key as keyof FormData], {
        shouldTouch: true,
        shouldValidate: true,
      });
    });
  };

  const submitHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    const formData = getValues();

    touchAllFields(formData);

    handleSubmit(onSubmit)(e);
  };

  const watchedStatus = watch('newStatus');

  useEffect(() => {
    const formData = getValues();

    touchAllFields(formData);
  }, []);

  useEffect(() => {
    if (watchedStatus !== defaultData?.lastStatus?.status) {
      setValue('reason', '');
    } else {
      setValue('reason', initialReason);
    }
  }, [watchedStatus, setValue, initialReason, defaultData]);

  return (
    <SessionModalFormWrapper
      title={'Enrollment Status'}
      isLoading={isLoading}
      onCancel={onCancel}
      submitHandler={submitHandler}
    >
      <Select
        value={statusField.value}
        onSelect={(value) => statusField.onChange(value)}
        options={ENROLLMENT_STATUS_OPTIONS}
        onBlur={statusField.onBlur}
        label="Status"
        isValid={!statusFieldState.error}
        isTouched={statusFieldState.isTouched}
        errorText={statusFieldState.error?.message}
        isRequired
      />

      <TextArea id="reason" label="Reason" className="h-40" {...register('reason')} />
    </SessionModalFormWrapper>
  );
};

export default EnrollmentDetailsStatusForm;
